import {format} from 'date-fns'
import type {TFunction} from 'i18next'
import * as XLSX from 'xlsx-js-style'
import {CellObject, WorkSheet} from 'xlsx-js-style'
import {Fund} from '../types/Fund'
import {invRor} from './Helpers'

export const getLabelGenerated = (t: TFunction, fileDate: Date): string => {
    return t('label.xlsx.common.generated', {date: format(fileDate, 'dd.MM.yyyy'), time: format(fileDate, 'k:mm:ss')})
}

export const tableDataCentering = (ws: WorkSheet, headerRowForFundData: number, maxColNum: number): void => {
    const ref = String(ws['!ref'])
    const range = XLSX.utils.decode_range(ref)
    const toEndResults = range.e.r

    for (let row = headerRowForFundData; row <= toEndResults; row++) {
        for (let col = 1; col <= maxColNum; col++) {
            const cellAddress = XLSX.utils.encode_cell({r: row, c: col})
            ws[cellAddress] = ws[cellAddress] || {}
            ws[cellAddress].s = {
                alignment: {horizontal: 'center', vertical: 'center'},
            }
        }
    }
}

export const firstColIndent = (ws: WorkSheet): void => {
    const ref = String(ws['!ref'])
    const range = XLSX.utils.decode_range(ref)
    const toEndResults = range.e.r

    for (let row = 1; row <= toEndResults; row++) {
        const cellAddress = XLSX.utils.encode_cell({r: row, c: 0})
        ws[cellAddress] = ws[cellAddress] || {}
        const currStyle = ws[cellAddress]?.s
        ws[cellAddress].s = {
            ...currStyle,
            ...{
                alignment: {indent: 1, horizontal: 'left', vertical: 'center'},
            },
        }
    }
}

export const percentRorFormatter = (ws: WorkSheet, startCol: number, endCol: number, rowStart: number): void => {
    const ref = String(ws['!ref'])
    const range = XLSX.utils.decode_range(ref)
    const toEndResults = range.e.r
    for (let row = rowStart; row <= toEndResults; row++) {
        for (let col = startCol; col <= endCol; col++) {
            const cellAddress = XLSX.utils.encode_cell({r: row, c: col})
            ws[cellAddress] = ws[cellAddress] || {}
            if (typeof ws[cellAddress].v === 'number') {
                const currStyle = ws[cellAddress]?.s
                ws[cellAddress].s = {
                    ...currStyle,
                    ...{
                        numFmt: '0.00%',
                    },
                }
            }
        }
    }
}

export const setColWidth = (
    ws: WorkSheet,
    maxColNum: number,
    defaultWidth: number,
    customWidth: Array<number>
): void => {
    if (ws['!cols'])
        for (let col = 0; col < maxColNum; col++) {
            if (customWidth[col]) ws['!cols'].push({wch: customWidth[col]})
            else ws['!cols'].push({wch: defaultWidth})
        }
    else console.error('XLSX cols not defined - setting width suppressed')
}

export const rorListDownload = (
    listData: Array<Fund>,
    rorOptions: Array<string>,
    t: TFunction,
    fundKind: string,
    headerTitle: string,
    filename: string,
    fileDate: Date
): void => {
    const xlsxStruct: Array<any> = []
    listData.map((fund: Fund) => {
        const xlsxItem = {
            [t('label.xlsx.common.fund')]: fund.name,
            [t('label.xlsx.common.date')]: fund.valuationDate,
            [t('label.xlsx.common.value')]: fund.valuation,
            [t('label.xlsx.common.currency')]: fund.currency,
        }

        if (fundKind === 'fio') xlsxItem['SRI'] = fund.sri

        Object.keys(invRor).map((rorProp) => {
            if (rorOptions.includes(rorProp))
                xlsxItem[rorProp] = fund[invRor[rorProp]] ? Number((fund[invRor[rorProp]] / 100).toFixed(4)) : '-'
        })
        xlsxStruct.push(xlsxItem)
    })

    //Style XLSX
    const maxColNum = Object.keys(xlsxStruct[0]).length
    // const maxColNum = selectedRateOfReturnOptions.length + (fundKind === 'fio') ? 5 : 4
    const headerRowForFundData: number = 4
    const ws = XLSX.utils.json_to_sheet([])
    ws['!merges'] = []

    const tabTitle = t('label.xlsx.list.filename.' + fundKind)

    const additionalRows = [{}, {A: headerTitle}, {A: getLabelGenerated(t, fileDate)}]

    ws['!rows'] = [{hpx: 25}, {hpx: 20}, {hpx: 20}, {hpx: 30}]
    XLSX.utils.sheet_add_json(ws, additionalRows, {skipHeader: true, origin: 0})
    XLSX.utils.sheet_add_json(ws, xlsxStruct, {origin: additionalRows.length})

    ws['A2'].s = {
        font: {sz: 18, color: {rgb: '006192'}},
        // alignment: { indent: 1, horizontal: "left" }
    }

    ws['A3'].s = {
        font: {sz: 11, color: {rgb: '767676'}},
    }

    ws['!cols'] = []
    for (let col = 0; col < maxColNum; col++) {
        const cellName = String.fromCharCode(col + 65) + '' + headerRowForFundData
        ws[cellName].s = {
            font: {sz: 10, bold: true},
            alignment: {horizontal: 'center', vertical: 'center'},
            border: {
                bottom: {style: 'thin', color: {rgb: '000000'}},
            },
        }
    }

    setColWidth(ws, maxColNum, 15, [50, 20, 20])
    tableDataCentering(ws, headerRowForFundData, maxColNum)
    firstColIndent(ws)

    const startRorCol = fundKind === 'fio' ? 5 : 4
    percentRorFormatter(ws, startRorCol, maxColNum, headerRowForFundData)

    // white background and hidden borders for header
    ws['!merges']?.push(XLSX.utils.decode_range('A2:E2'))
    ws['!merges']?.push(XLSX.utils.decode_range('A3:E3'))

    setFontFamily(ws, 'Arial')
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, tabTitle)
    XLSX.writeFile(wb, filename)
}

export const setFontFamily =  function (worksheet: WorkSheet, font:string = 'Arial'): void {
    if (!worksheet['!ref']) return;
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const cellAddress = { c: C, r: R };
            const cellRef = XLSX.utils.encode_cell(cellAddress);
            const cell: CellObject | undefined = worksheet[cellRef];
            if (cell && cell.v !== undefined) {
                if (!cell.s) cell.s = {};
                if (!cell.s.font) cell.s.font = {};
                if (!cell.s.font.name) cell.s.font.name = font;
            }
        }
    }
}